import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { JjamesButtonComponent, JJamesPaginationConfig, JJamesPaginatorComponent } from 'shared-lib';
import {
    CashBoxEmptyingDto,
    CoinMachineDto,
    CoinMachineService,
    CoinMachineTrendDto,
    ParkingSpaceDto
} from '../../../api/gen';
import {
    EmptyCashBoxDialogComponent,
    EmptyCashBoxDialogData,
    EmptyCashBoxDialogResult
} from './empty-cash-box-dialog/empty-cash-box-dialog.component';

export type CoinMachinePeriod = '30d' | '90d' | '365d';

export interface CoinMachineChartColumn {
    date: string;
    label: string;
    showLabel: boolean;
    amount: number;
    coins: number;
    fill: number;
    fillPct: number;
    barHeightPct: number;
    emptied: boolean;
}

const PERIOD_DAYS: Record<CoinMachinePeriod, number> = { '30d': 30, '90d': 90, '365d': 365 };

/**
 * The payments page's coin machine tab: what each cash box holds, when it was emptied, and how it
 * filled up.
 *
 * Request budget: one list request per site change, then a trend and one history page for the
 * selected machine only — never a request per card. Nothing polls; the list reloads after an emptying.
 */
@Component({
    selector: 'app-coin-machines',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        JjamesButtonComponent,
        JJamesPaginatorComponent,
        TranslatePipe
    ],
    templateUrl: './coin-machines.component.html',
    styleUrl: './coin-machines.component.scss'
})
export class CoinMachinesComponent {
    /** Site narrowing from the parent page. Empty means every site in the caller's scope. */
    readonly parkingSpaceId = input<string>('');
    /** The parent's site list, to name a machine's site when all sites are shown. */
    readonly sites = input<ParkingSpaceDto[]>([]);

    private coinMachineService = inject(CoinMachineService);
    private dialog = inject(MatDialog);
    private snackBar = inject(MatSnackBar);
    private translate = inject(TranslateService);

    readonly isLoading = signal(false);
    readonly machines = signal<CoinMachineDto[]>([]);
    readonly selectedId = signal<string | null>(null);
    readonly emptyingId = signal<string | null>(null);

    readonly period = signal<CoinMachinePeriod>('30d');
    readonly periods: { key: CoinMachinePeriod; labelKey: string }[] = [
        { key: '30d', labelKey: 'reports.period30d' },
        { key: '90d', labelKey: 'reports.period90d' },
        { key: '365d', labelKey: 'coinMachines.period365d' }
    ];

    readonly trend = signal<CoinMachineTrendDto | undefined>(undefined);
    readonly isTrendLoading = signal(false);
    readonly activeColumn = signal<CoinMachineChartColumn | null>(null);

    readonly emptyings = signal<CashBoxEmptyingDto[]>([]);
    readonly emptyingsTotal = signal(0);
    readonly isHistoryLoading = signal(false);
    readonly pageIndex = signal(0);
    readonly pageSize = signal(10);

    private listSub?: Subscription;
    private trendSub?: Subscription;
    private historySub?: Subscription;

    readonly selected = computed(() =>
        this.machines().find(m => m.paymentTerminalId === this.selectedId()) ?? null);

    readonly showSiteName = computed(() => !this.parkingSpaceId());

    private readonly siteNames = computed(() =>
        new Map(this.sites().map(s => [s.id ?? '', s.name ?? ''])));

    readonly pagination = computed<JJamesPaginationConfig>(() => ({
        currentPage: this.pageIndex(),
        totalPages: Math.max(1, Math.ceil(this.emptyingsTotal() / this.pageSize())),
        pageSize: this.pageSize(),
        totalItems: this.emptyingsTotal(),
        itemsPerPage: this.pageSize(),
        pageSizeOptions: [10, 20, 50]
    }));

    readonly chartColumns = computed<CoinMachineChartColumn[]>(() => {
        const trend = this.trend();
        const points = trend?.points ?? [];
        if (points.length === 0) return [];

        const capacity = Math.max(1, trend?.capacityInCents ?? 1);
        const highestAmount = Math.max(1, ...points.map(p => p.amountInCents ?? 0));
        const labelEvery = Math.max(1, Math.ceil(points.length / 12));

        return points.map((p, i) => {
            const amount = (p.amountInCents ?? 0) / 100;
            const date = p.date ? new Date(p.date) : null;
            return {
                date: p.date ?? String(i),
                label: date ? date.toLocaleDateString(undefined, { timeZone: 'UTC', day: 'numeric', month: 'short' }) : '—',
                showLabel: i % labelEvery === 0 || i === points.length - 1,
                amount,
                coins: p.coinCount ?? 0,
                fill: (p.fillInCents ?? 0) / 100,
                fillPct: Math.round(((p.fillInCents ?? 0) / capacity) * 100),
                barHeightPct: amount > 0 ? Math.max(3, ((p.amountInCents ?? 0) / highestAmount) * 100) : 0,
                emptied: !!p.emptied
            };
        });
    });

    /** The fill line's vertical scale: 0 to 100 % of the capacity, or higher when the box overflowed. */
    private readonly fillScaleTop = computed(() =>
        Math.max(100, ...this.chartColumns().map(c => c.fillPct)));

    /** Polyline points in a viewBox of one unit per day by 100. */
    readonly fillLinePoints = computed(() => {
        const top = this.fillScaleTop();
        return this.chartColumns()
            .map((c, i) => `${i + 0.5},${100 - (c.fillPct / top) * 100}`)
            .join(' ');
    });

    readonly thresholdY = computed(() =>
        100 - ((this.trend()?.alertThresholdPercent ?? 80) / this.fillScaleTop()) * 100);

    readonly chartMinWidth = computed(() => {
        const count = this.chartColumns().length;
        return count === 0 ? 0 : Math.max(500, count * (count > 90 ? 6 : 18));
    });

    readonly totalIntakeInPeriod = computed(() =>
        this.chartColumns().reduce((sum, c) => sum + c.amount, 0));

    private readonly reloadOnSiteChange = effect(() => {
        this.parkingSpaceId();
        untracked(() => this.loadMachines());
    });

    private readonly reloadTrend = effect(() => {
        const id = this.selectedId();
        this.period();
        untracked(() => this.loadTrend(id));
    });

    private readonly reloadHistory = effect(() => {
        const id = this.selectedId();
        this.pageIndex();
        this.pageSize();
        untracked(() => this.loadHistory(id));
    });

    siteName(machine: CoinMachineDto): string {
        return machine.parkingSpaceId ? this.siteNames().get(machine.parkingSpaceId) ?? '' : '';
    }

    euros(cents: number | null | undefined): number {
        return (cents ?? 0) / 100;
    }

    /**
     * The station's health as the kiosk sees it. An "Ok" older than the online window is not Ok any
     * more — the kiosk stops offering coins — so it reads Offline rather than Ok with a grey dot.
     */
    statusTone(machine: CoinMachineDto): 'online' | 'fault' | 'idle' {
        if (!machine.isEnabled) return 'idle';
        if (machine.isOnline) return 'online';
        return machine.status === 'Fault' || machine.status === 'Missing' ? 'fault' : 'idle';
    }

    statusLabelKey(machine: CoinMachineDto): string {
        if (!machine.isEnabled) return 'coinMachines.disabled';
        if (machine.isOnline) return 'coinMachines.statusOk';
        switch (machine.status) {
            case 'Fault': return 'coinMachines.statusFault';
            case 'Missing': return 'coinMachines.statusMissing';
            default: return 'coinMachines.statusOffline';
        }
    }

    /** Emerald below the alert level, amber from it, rose once the box is full. */
    fillTone(machine: CoinMachineDto): 'ok' | 'warn' | 'full' {
        const pct = machine.fillPercent ?? 0;
        if (pct >= 100) return 'full';
        return machine.isAboveAlertThreshold ? 'warn' : 'ok';
    }

    select(machine: CoinMachineDto): void {
        if (this.selectedId() === machine.paymentTerminalId) return;
        this.pageIndex.set(0);
        this.selectedId.set(machine.paymentTerminalId ?? null);
    }

    setPeriod(period: CoinMachinePeriod): void {
        this.period.set(period);
    }

    onPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.pageIndex.set(event.pageIndex);
        this.pageSize.set(event.pageSize);
    }

    openEmptyDialog(machine: CoinMachineDto, event?: Event): void {
        event?.stopPropagation();
        if (this.emptyingId()) return;

        const dialogRef = this.dialog.open<EmptyCashBoxDialogComponent, EmptyCashBoxDialogData, EmptyCashBoxDialogResult>(
            EmptyCashBoxDialogComponent, {
                width: '480px',
                maxWidth: '95vw',
                data: { machine }
            });

        dialogRef.afterClosed().subscribe(result => {
            if (result) this.empty(machine, result.comment);
        });
    }

    private empty(machine: CoinMachineDto, comment: string): void {
        const terminalId = machine.paymentTerminalId;
        if (!terminalId) return;

        this.emptyingId.set(terminalId);
        this.coinMachineService.coinMachinesPaymentTerminalIdEmptyingsPost(terminalId, {
            lastEmptyingId: machine.lastEmptying?.id ?? null,
            comment: comment || null
        }).subscribe({
            next: () => {
                this.emptyingId.set(null);
                this.snackBar.open(this.translate.instant('coinMachines.emptied'), undefined, { duration: 3000 });
                this.loadMachines();
                if (this.selectedId() === terminalId) {
                    this.loadTrend(terminalId);
                    // Back to the first page, where the new row is. Changing the page reloads the
                    // history through its effect; only an unchanged page needs the explicit call.
                    if (this.pageIndex() === 0) this.loadHistory(terminalId);
                    else this.pageIndex.set(0);
                }
            },
            error: (err) => {
                this.emptyingId.set(null);
                // MadResult errors arrive as a plain-string body ("Title.Detail").
                const body = typeof err?.error === 'string' ? err.error.trim() : '';
                this.snackBar.open(body || this.translate.instant('coinMachines.emptyFailed'), 'OK', { duration: 6000 });
                // A refusal usually means someone else emptied it; show them what is there now.
                this.loadMachines();
            }
        });
    }

    private loadMachines(): void {
        this.listSub?.unsubscribe();
        this.isLoading.set(this.machines().length === 0);

        this.listSub = this.coinMachineService.coinMachinesGet(this.parkingSpaceId() || undefined).subscribe({
            next: (machines) => {
                this.machines.set(machines ?? []);
                this.isLoading.set(false);
                const stillThere = this.machines().some(m => m.paymentTerminalId === this.selectedId());
                if (!stillThere) this.selectedId.set(this.machines()[0]?.paymentTerminalId ?? null);
            },
            error: (err) => {
                console.error('Failed to load coin machines', err);
                this.machines.set([]);
                this.isLoading.set(false);
                this.snackBar.open(this.translate.instant('coinMachines.loadFailed'), 'OK', { duration: 5000 });
            }
        });
    }

    private loadTrend(terminalId: string | null): void {
        this.trendSub?.unsubscribe();
        this.activeColumn.set(null);
        if (!terminalId) {
            this.trend.set(undefined);
            return;
        }

        const { from, to } = this.window(this.period());
        this.isTrendLoading.set(true);
        this.trendSub = this.coinMachineService
            .coinMachinesPaymentTerminalIdTrendGet(terminalId, from.toISOString(), to.toISOString())
            .subscribe({
                next: (trend) => {
                    this.trend.set(trend);
                    this.isTrendLoading.set(false);
                },
                error: (err) => {
                    console.error('Failed to load the coin machine trend', err);
                    this.trend.set(undefined);
                    this.isTrendLoading.set(false);
                }
            });
    }

    private loadHistory(terminalId: string | null): void {
        this.historySub?.unsubscribe();
        if (!terminalId) {
            this.emptyings.set([]);
            this.emptyingsTotal.set(0);
            return;
        }

        this.isHistoryLoading.set(true);
        this.historySub = this.coinMachineService
            .coinMachinesPaymentTerminalIdEmptyingsGet(terminalId, this.pageIndex() + 1, this.pageSize())
            .subscribe({
                next: (page) => {
                    this.emptyings.set([...(page?.items ?? [])]);
                    this.emptyingsTotal.set(page?.totalCount ?? 0);
                    this.isHistoryLoading.set(false);
                },
                error: (err) => {
                    console.error('Failed to load the cash box emptyings', err);
                    this.emptyings.set([]);
                    this.emptyingsTotal.set(0);
                    this.isHistoryLoading.set(false);
                }
            });
    }

    /** Whole UTC days ending today, matching the backend's daily buckets. */
    private window(period: CoinMachinePeriod): { from: Date; to: Date } {
        const now = new Date();
        const tomorrow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
        const from = new Date(tomorrow.getTime() - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000);
        return { from, to: tomorrow };
    }
}
